import React from 'react';

export function ShapingIssues() {
  return (
    <div className="space-y-6 text-white/90" style={{ userSelect: "none" }}>
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Common Shaping Issues</h3>
        <p className="mb-4">
          Proper shaping is crucial for achieving good oven spring, crumb structure, and overall bread aesthetics. Understanding common shaping issues and their solutions will help you create better-looking and better-tasting bread.
        </p>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Lack of Surface Tension</h3>
        
        <div className="space-y-4">
          <div className="bg-white/5 p-4 rounded-lg">
            <h4 className="text-lg text-amber-400 mb-2">Symptoms</h4>
            <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
              <li>Flat, spread-out loaves</li>
              <li>Poor oven spring</li>
              <li>Irregular scoring patterns</li>
              <li>Uneven crust development</li>
            </ul>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="text-lg text-amber-400 mb-2">Common Causes</h4>
              <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
                <li>Insufficient pre-shaping</li>
                <li>Over-flouring the work surface</li>
                <li>Weak gluten development</li>
                <li>Improper shaping technique</li>
                <li>Over-proofed dough</li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg text-amber-400 mb-2">Solutions</h4>
              <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
                <li>Perform proper pre-shape and rest</li>
                <li>Use minimal flour during shaping</li>
                <li>Develop gluten properly during mixing</li>
                <li>Practice proper shaping technique</li>
                <li>Monitor proofing time carefully</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Seam Issues</h3>
        
        <div className="space-y-4">
          <div className="bg-white/5 p-4 rounded-lg">
            <h4 className="text-lg text-amber-400 mb-2">Types of Seam Problems</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h5 className="text-amber-400 text-sm">Weak Seams:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Loaf bursts at seams during baking</li>
                  <li>Uneven expansion</li>
                  <li>Poor final shape</li>
                </ul>
              </div>
              <div>
                <h5 className="text-amber-400 text-sm">Visible Seams:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Obvious seam lines on finished bread</li>
                  <li>Uneven browning</li>
                  <li>Aesthetic issues</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="bg-white/5 p-4 rounded-lg">
            <h4 className="text-lg text-amber-400 mb-2">Solutions</h4>
            <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
              <li>Pinch seams firmly during shaping</li>
              <li>Place seam-side up in banneton for boules</li>
              <li>Ensure proper surface tension</li>
              <li>Don't over-flour seams</li>
              <li>Practice consistent shaping technique</li>
            </ul>
          </div>
        </div>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Dough Sticking Issues</h3>
        
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white/5 p-4 rounded-lg">
              <h4 className="text-lg text-amber-400 mb-2">During Shaping</h4>
              <div>
                <h5 className="text-amber-400 text-sm">Causes:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Over-hydrated dough</li>
                  <li>Insufficient bench flour</li>
                  <li>Warm dough temperature</li>
                  <li>Poor gluten development</li>
                </ul>
              </div>
              <div className="mt-3">
                <h5 className="text-amber-400 text-sm">Solutions:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Use wet hands instead of flour</li>
                  <li>Chill dough if necessary</li>
                  <li>Work quickly and confidently</li>
                  <li>Use bench scraper effectively</li>
                </ul>
              </div>
            </div>
            
            <div className="bg-white/5 p-4 rounded-lg">
              <h4 className="text-lg text-amber-400 mb-2">During Transfer</h4>
              <div>
                <h5 className="text-amber-400 text-sm">Causes:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Under-floured banneton</li>
                  <li>Over-proofed dough</li>
                  <li>Poor banneton preparation</li>
                  <li>High hydration dough</li>
                </ul>
              </div>
              <div className="mt-3">
                <h5 className="text-amber-400 text-sm">Solutions:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Use rice flour in banneton</li>
                  <li>Monitor proofing carefully</li>
                  <li>Season bannetons properly</li>
                  <li>Use parchment paper if needed</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Shaping Different Dough Types</h3>
        
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-white/80">
            <thead>
              <tr className="border-b border-white/20">
                <th className="text-left py-3 px-4 text-amber-400">Dough Type</th>
                <th className="text-left py-3 px-4 text-amber-400">Common Issues</th>
                <th className="text-left py-3 px-4 text-amber-400">Special Considerations</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-white/10">
                <td className="py-3 px-4 font-medium">High Hydration</td>
                <td className="py-3 px-4">Excessive sticking, hard to handle</td>
                <td className="py-3 px-4">Use wet hands, minimal flour, quick movements</td>
              </tr>
              <tr className="border-b border-white/10">
                <td className="py-3 px-4 font-medium">Whole Grain</td>
                <td className="py-3 px-4">Weak structure, tears easily</td>
                <td className="py-3 px-4">Gentle handling, shorter bench rest</td>
              </tr>
              <tr className="border-b border-white/10">
                <td className="py-3 px-4 font-medium">Enriched Dough</td>
                <td className="py-3 px-4">Soft, loses shape easily</td>
                <td className="py-3 px-4">Cold proof, firm shaping, well-floured</td>
              </tr>
              <tr>
                <td className="py-3 px-4 font-medium">Rye Heavy</td>
                <td className="py-3 px-4">Sticky, minimal structure</td>
                <td className="py-3 px-4">Wet hands, minimal handling, use forms</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-amber-400 mb-4">Pre-shaping and Bench Rest</h3>
        
        <div className="space-y-4">
          <div>
            <h4 className="text-lg text-white mb-2">Common Pre-shaping Mistakes</h4>
            <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
              <li>Skipping pre-shape entirely</li>
              <li>Too much or too little flour</li>
              <li>Insufficient surface tension</li>
              <li>Uneven shaping pressure</li>
              <li>Poor dough orientation tracking</li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-lg text-white mb-2">Bench Rest Issues</h4>
            <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
              <li>Too short: Dough fights back during final shape</li>
              <li>Too long: Dough loses structure</li>
              <li>Uneven rest: Inconsistent final shape</li>
              <li>Improper covering: Skin formation</li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-lg text-white mb-2">Solutions</h4>
            <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
              <li>Always include pre-shaping step</li>
              <li>Rest 20-30 minutes for most doughs</li>
              <li>Cover dough during rest</li>
              <li>Adjust rest time based on dough strength</li>
              <li>Mark dough orientation for consistent shaping</li>
            </ul>
          </div>
        </div>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Advanced Shaping Troubleshooting</h3>
        
        <div className="space-y-4">
          <div className="bg-white/5 p-4 rounded-lg">
            <h4 className="text-lg text-amber-400 mb-2">Batard/Oval Shapes</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h5 className="text-amber-400 text-sm">Common Issues:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Uneven thickness</li>
                  <li>Tapered ends</li>
                  <li>Poor seam closure</li>
                  <li>Loss of shape during proofing</li>
                </ul>
              </div>
              <div>
                <h5 className="text-amber-400 text-sm">Solutions:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Even pressure during shaping</li>
                  <li>Maintain rectangular shape</li>
                  <li>Proper seam sealing</li>
                  <li>Support sides during proofing</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="bg-white/5 p-4 rounded-lg">
            <h4 className="text-lg text-amber-400 mb-2">Boule/Round Shapes</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h5 className="text-amber-400 text-sm">Common Issues:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Irregular roundness</li>
                  <li>Bottom seam opening</li>
                  <li>Flat spots</li>
                  <li>Uneven rise</li>
                </ul>
              </div>
              <div>
                <h5 className="text-amber-400 text-sm">Solutions:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Use circular motions</li>
                  <li>Proper seam placement</li>
                  <li>Even tension all around</li>
                  <li>Consistent dragging motion</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}